/**
 * The TidalVenue mark and lockup, drawn inline.
 *
 * Inline rather than an <img> pointing at /brand/icon.svg for three reasons: it is one fewer
 * request on the first paint of every signed-out page, it inherits the size its container gives it
 * without a second source of truth for dimensions, and it cannot show a broken-image glyph if the
 * asset ever fails to load. The geometry comes from ./mark, which is also what generates the
 * standalone files, so the mark in the product and the mark in the browser tab cannot disagree.
 *
 * The gradient and clip-path identifiers are per instance. SVG resolves url(#name) against the
 * whole document, so two marks on one page sharing a literal id would be two elements claiming the
 * same name: legal-looking, and quietly wrong the moment the two differ. useId gives each instance
 * its own, and the colons React puts in that value are stripped because they are awkward in a
 * fragment identifier even where they are technically permitted.
 */

import { useId } from "react";
import {
  brandWordmark,
  canvas,
  pinPath,
  tileGradient,
  tileRadius,
  waveBackOpacity,
  waveBackPath,
  waveFrontPath,
  windowCircle,
} from "./mark";

interface BrandMarkProps {
  /** Defaults to the shared sizing class. Pass a different one to size it somewhere else. */
  className?: string;
  /**
   * An accessible name. Left out by default: the mark almost always sits next to the product name
   * as ordinary text, and naming it there too would have a screen reader announce the brand twice.
   */
  title?: string;
}

export function BrandMark({ className = "brand-mark", title }: BrandMarkProps) {
  const instance = useId().replace(/:/gu, "");
  const gradient = `${instance}-tile`;
  const windowClip = `${instance}-window`;

  const artwork = (
    <>
      <defs>
        <linearGradient
          id={gradient}
          x1={tileGradient.x1}
          y1={tileGradient.y1}
          x2={tileGradient.x2}
          y2={tileGradient.y2}
          gradientUnits="userSpaceOnUse"
        >
          {tileGradient.stops.map((stop) => (
            <stop key={stop.offset} offset={stop.offset} stopColor={stop.color} />
          ))}
        </linearGradient>
        <clipPath id={windowClip}>
          <circle cx={windowCircle.cx} cy={windowCircle.cy} r={windowCircle.r} />
        </clipPath>
      </defs>
      <rect width={canvas} height={canvas} rx={tileRadius} fill={`url(#${gradient})`} />
      <path d={pinPath} fill="#FFFFFF" />
      <circle
        cx={windowCircle.cx}
        cy={windowCircle.cy}
        r={windowCircle.r}
        fill={`url(#${gradient})`}
      />
      <g clipPath={`url(#${windowClip})`}>
        <path d={waveBackPath} fill="#FFFFFF" opacity={waveBackOpacity} />
        <path d={waveFrontPath} fill="#FFFFFF" />
      </g>
    </>
  );

  // The two presentations are written out in full rather than folded into conditional attributes.
  // A named mark needs role="img" and a <title>; a decorative one needs aria-hidden and no title at
  // all. Expressed as `aria-hidden={title ? undefined : true}` both facts become invisible to any
  // static check, which is precisely what Biome's noSvgWithoutTitle objected to: an SVG that is
  // sometimes named and sometimes hidden is, to a reader and to a linter, provably neither.
  if (title) {
    return (
      <svg
        className={className}
        viewBox={`0 0 ${canvas} ${canvas}`}
        xmlns="http://www.w3.org/2000/svg"
        role="img"
      >
        <title>{title}</title>
        {artwork}
      </svg>
    );
  }

  return (
    <svg
      className={className}
      viewBox={`0 0 ${canvas} ${canvas}`}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {artwork}
    </svg>
  );
}

/**
 * The mark next to the product name.
 *
 * The name is text rather than artwork on purpose: selectable, searchable, translatable, and read
 * by a screen reader as the one word it is, since nothing separates the two halves. It is set in
 * Outfit through the .brand-wordmark rule in web/src/styles.css, the face the source lockup uses and
 * which the product serves from its own origin, in the lockup's two tones: the first word in the
 * surrounding text color, the second in the accent of wherever it sits.
 */
export function BrandLockup({ className }: { className?: string }) {
  return (
    <span className={className ? `brand-lockup ${className}` : "brand-lockup"}>
      <BrandMark />
      <strong className="brand-wordmark">
        {brandWordmark.lead}
        <span className="brand-wordmark-tail">{brandWordmark.tail}</span>
      </strong>
    </span>
  );
}
